import {
  IAddressSearchBounds,
  ICoordinate,
  IGeoJSON,
  isValidCoordinateTuple,
} from "@ryde/shared";

import {
  BOUNDS_EDGE_PRECISION,
  BOUNDS_MIN_SPAN_DEGREES,
  BOUNDS_ZOOM_MAX,
  BOUNDS_ZOOM_MIN,
  BOUNDS_ZOOM_WORLD_DEGREES,
  TBoundsCorner,
} from "./bounds-block.const";

/** Latitude and longitude limits the map lets a handle be dragged past. */
const MAX_LATITUDE = 90;
const MAX_LONGITUDE = 180;

/** Rounded on the way in from the map — a handle drags to full float precision. */
export const roundEdge = (edge: number): number =>
  Number(edge.toFixed(BOUNDS_EDGE_PRECISION));

/** A rectangle drawn as a closed GeoJSON ring, which is what the polygon editor loads. */
export const boundsToGeoJson = (bounds: IAddressSearchBounds): IGeoJSON => ({
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      properties: {},
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [bounds.west, bounds.south],
            [bounds.east, bounds.south],
            [bounds.east, bounds.north],
            [bounds.west, bounds.north],
            [bounds.west, bounds.south],
          ],
        ],
      },
    },
  ],
});

/**
 * One dragged corner, applied as a crop.
 *
 * A corner owns exactly one latitude and one longitude, so moving it keeps the area a
 * rectangle by construction — there is no shape to snap back from. Both edges are clamped
 * against their opposites, because a corner dragged past one would enclose no area, and
 * against the coordinate range, because the map lets you drag off the top of the world.
 */
export const moveBoundsCorner = ({
  bounds,
  corner,
  latitude,
  longitude,
}: {
  bounds: IAddressSearchBounds;
  corner: TBoundsCorner;
  latitude: number;
  longitude: number;
}): IAddressSearchBounds => {
  const isNorth = corner.latitude === "north";
  const isEast = corner.longitude === "east";

  const latitudeLimit = isNorth
    ? bounds.south + BOUNDS_MIN_SPAN_DEGREES
    : bounds.north - BOUNDS_MIN_SPAN_DEGREES;
  const longitudeLimit = isEast
    ? bounds.west + BOUNDS_MIN_SPAN_DEGREES
    : bounds.east - BOUNDS_MIN_SPAN_DEGREES;

  const clampedLatitude = isNorth
    ? Math.min(Math.max(latitude, latitudeLimit), MAX_LATITUDE)
    : Math.max(Math.min(latitude, latitudeLimit), -MAX_LATITUDE);
  const clampedLongitude = isEast
    ? Math.min(Math.max(longitude, longitudeLimit), MAX_LONGITUDE)
    : Math.max(Math.min(longitude, longitudeLimit), -MAX_LONGITUDE);

  return {
    ...bounds,
    [corner.latitude]: roundEdge(clampedLatitude),
    [corner.longitude]: roundEdge(clampedLongitude),
  };
};

/**
 * Whether the four edges enclose an area the backend will send; anything else is not saveable.
 *
 * The two corners are range-checked as coordinates rather than edge by edge, so latitude and
 * longitude limits come from one shared validator instead of being restated here.
 */
export const isValidAddressSearchBounds = (
  bounds: IAddressSearchBounds | undefined,
): bounds is IAddressSearchBounds => {
  if (!bounds) {
    return false;
  }

  const { north, south, east, west } = bounds;

  return (
    isValidCoordinateTuple([south, west]) &&
    isValidCoordinateTuple([north, east]) &&
    north > south &&
    east > west
  );
};

/** Whether two rectangles describe the same area, so an unchanged map read can be ignored. */
export const isSameBounds = (
  first: IAddressSearchBounds | undefined,
  second: IAddressSearchBounds | undefined,
): boolean =>
  first?.north === second?.north &&
  first?.south === second?.south &&
  first?.east === second?.east &&
  first?.west === second?.west;

/**
 * Which line to show beside the buttons, as a key under `systemParam.addressSearchBounds`.
 *
 * The disabled Save has to explain itself: a box that encloses no area cannot be sent, and a
 * silent dead button reads as a broken one.
 */
export const getBoundsHintKey = ({
  bounds,
  isEditing,
  isSaveable,
}: {
  bounds: IAddressSearchBounds | undefined;
  isEditing: boolean;
  isSaveable: boolean;
}): string => {
  if (!bounds) {
    return "noLimit";
  }

  if (!isEditing) {
    return "areaSet";
  }

  return isSaveable ? "dragHint" : "invalidArea";
};

/** Where the map opens: the middle of the rectangle. */
export const getBoundsCenter = (bounds: IAddressSearchBounds): ICoordinate => ({
  lat: (bounds.north + bounds.south) / 2,
  lng: (bounds.east + bounds.west) / 2,
});

/**
 * A zoom that frames the rectangle when the map opens.
 *
 * Derived from the wider span rather than fitted through the map, so the first paint is
 * already close: each zoom level halves the degrees on screen, and the tile grid spans 360°
 * of longitude at zoom 0. One level is given back as margin so the corner handles are not
 * flush against the canvas edge.
 */
export const getBoundsZoom = (bounds: IAddressSearchBounds): number => {
  const longitudeSpan = Math.abs(bounds.east - bounds.west);
  const latitudeSpan = Math.abs(bounds.north - bounds.south);
  const span = Math.max(longitudeSpan, latitudeSpan, BOUNDS_MIN_SPAN_DEGREES);
  const zoom = Math.log2(BOUNDS_ZOOM_WORLD_DEGREES / span) - 1;

  return Math.min(Math.max(Math.floor(zoom), BOUNDS_ZOOM_MIN), BOUNDS_ZOOM_MAX);
};

/**
 * Reads a rectangle out of whatever the form hands over, or nothing when it is not one.
 *
 * The four edges are coerced before they are checked: a form value and a seeded
 * `newItemValue` both travel as JSON, and a number that was edited through a field can come
 * back as a string. Validating first would quietly reject a perfectly good rectangle — which
 * is what hides "Reset to default" and the map itself.
 */
export const toAddressSearchBounds = (
  candidate: unknown,
): IAddressSearchBounds | undefined => {
  if (!candidate || typeof candidate !== "object") {
    return undefined;
  }

  const { north, south, east, west } = candidate as Record<string, unknown>;
  const bounds: IAddressSearchBounds = {
    north: Number(north),
    south: Number(south),
    east: Number(east),
    west: Number(west),
  };

  return isValidAddressSearchBounds(bounds) ? bounds : undefined;
};
